import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ORG_DB_NAMES, ORG_LABELS, TEAM_LABELS } from '../config';
import { fmtDate, fmtMoney, useFetch, usePaged } from '../hooks';
import { Badge, Button, Check, Input, Select, SearchBox } from '../ui/basics';
import { DataTable } from '../ui/DataTable';

export const teamName = (lead) => TEAM_LABELS[lead] || lead || '—';

const ORG_BY_DB_NAME = Object.fromEntries(Object.entries(ORG_DB_NAMES).map(([key, name]) => [name, key]));
const ORG_COLORS = { della: '#1f75e0', 'vrd canada': '#01497c', vezita: '#e8770a' };

/** Organization name with a dot in that organization's colour. */
export const OrgBadge = ({ name }) => {
  const key = ORG_BY_DB_NAME[name];
  return (
    <span className="row" style={{ gap: 6, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
      <i style={{ width: 8, height: 8, borderRadius: '50%', background: ORG_COLORS[key] || '#98a2b3', display: 'inline-block' }} />
      {ORG_LABELS[key] || name || '—'}
    </span>
  );
};

/** Table columns for a placements list. The organization column only appears when several are mixed. */
const real = (d) => (d && String(d) >= '1900-01-01' ? new Date(String(d).slice(0, 10)) : null);
/** Whole days between the real end and the projected end, or null unless both are real dates and it ended early. */
export const daysEarly = (r) => {
  const [end, planned] = [real(r.project_end), real(r.projected_end_date)];
  return end && planned && end < planned ? Math.round((planned - end) / 86400000) : null;
};

export const placementColumns = ({ combined = false, showProjected = false, showEarly = false } = {}) => [
  { key: 'candidate_name', label: 'Candidate', sortable: true, render: (r) => <b>{r.candidate_name}</b> },
  ...(combined ? [{ key: 'organization', label: 'Organization', sortable: true, render: (r) => <OrgBadge name={r.organization} /> }] : []),
  { key: 'date_of_joining', label: 'Start', sortable: true, render: (r) => fmtDate(r.date_of_joining) },
  { key: 'project_end', label: 'End', sortable: true, render: (r) => fmtDate(r.project_end) },
  ...(showProjected ? [{ key: 'projected_end_date', label: 'Projected end', sortable: true, render: (r) => fmtDate(r.projected_end_date) }] : []),
  ...(showEarly ? [{ key: 'days_early', label: 'Days early', align: 'right', render: (r) => { const d = daysEarly(r); return d === null ? '—' : <Badge tone={d >= 90 ? 'danger' : d >= 30 ? 'warning' : ''}>{d} {d === 1 ? 'day' : 'days'}</Badge>; } }] : []),
  { key: 'client', label: 'Client', sortable: true },
  { key: 'recruiter', label: 'Recruiter', sortable: true },
  { key: 'lead', label: 'Team', sortable: true, render: (r) => teamName(r.lead) },
  { key: 'active_inactive', label: 'Status', sortable: true,
    render: (r) => <Badge tone={r.active_inactive === 'Active' ? 'success' : ''}>{r.active_inactive || '—'}</Badge> },
  { key: 'gross_margin', label: 'Margin', align: 'right', render: (r) => fmtMoney(r.gross_margin) },
  { key: 'bill_rate', label: 'Bill', align: 'right', render: (r) => fmtMoney(r.bill_rate) },
  { key: 'pay_rate', label: 'Pay', align: 'right', render: (r) => fmtMoney(r.pay_rate) },
  { key: 'loc', label: 'Location' },
];

/** Server-paginated placements list. `fixed` filters (organization, lead, dates…) are always applied. */
export default function PlacementsTable({ fixed, initialFilters, onRowClick, toolbar = true, initialSize = 25, extra, showProjected, showEarly }) {
  const { teams, allTeams } = useAuth();
  const paged = usePaged('placements/', { fixed, initialFilters, initialSize, initialOrdering: '-date_of_joining' });
  const { filters, setFilter, clearFilters } = paged;
  const [more, setMore] = useState(false);
  const facets = useFetch(toolbar ? 'placements/facets/' : null, { organization: fixed.organization });
  const teamOptions = (allTeams ? Object.keys(TEAM_LABELS) : teams).map((t) => ({ value: t, label: TEAM_LABELS[t] }));
  const active = Object.values(filters).some(Boolean);
  const columns = placementColumns({ combined: fixed.organization === 'combined', showProjected: showProjected || Boolean(filters.ended_early), showEarly: showEarly || Boolean(filters.ended_early) });

  return (
    <>
      {toolbar && (
        <div className="toolbar">
          <SearchBox value={filters.search} onChange={(v) => setFilter('search', v)} placeholder="Search candidate, client or recruiter…" />
          {!fixed.lead && teamOptions.length > 1 && (
            <Select value={filters.lead || ''} onChange={(e) => setFilter('lead', e.target.value)} options={teamOptions} placeholder="All teams" aria-label="Team" />
          )}
          <Select value={filters.status || ''} onChange={(e) => setFilter('status', e.target.value)} options={['Active', 'Inactive']} placeholder="Any status" aria-label="Status" />
          <Select value={filters.client || ''} onChange={(e) => setFilter('client', e.target.value)}
            options={facets.data ? facets.data.clients : []} placeholder="All clients" aria-label="Client" />
          <Select value={filters.recruiter || ''} onChange={(e) => setFilter('recruiter', e.target.value)}
            options={facets.data ? facets.data.recruiters : []} placeholder="All recruiters" aria-label="Recruiter" />
          <Select value={filters.loc || ''} onChange={(e) => setFilter('loc', e.target.value)}
            options={facets.data ? facets.data.locations : []} placeholder="All locations" aria-label="Location" />
          <Button variant="ghost" size="sm" onClick={() => setMore(!more)}>{more ? 'Fewer filters' : 'More filters'}</Button>
          {active && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
          <div className="right">{extra}</div>
        </div>
      )}
      {toolbar && more && (
        <div className="toolbar">
          {[['joined_from', 'Started from'], ['joined_to', 'Started to'], ['ended_from', 'Ended from'], ['ended_to', 'Ended to']].map(([key, label]) => (
            <label key={key} className="row" style={{ gap: 6 }}><span className="muted">{label}</span>
              <Input type="date" style={{ width: 150 }} value={filters[key] || ''} onChange={(e) => setFilter(key, e.target.value)} /></label>
          ))}
          <Check label="Ended before projected end" checked={Boolean(filters.ended_early)} onChange={(e) => { setFilter('ended_early', e.target.checked ? '1' : ''); if (!e.target.checked) setFilter('early_min_days', ''); }} />
          {filters.ended_early && (
            <label className="row" style={{ gap: 6 }}><span className="muted">At least</span>
              <Input type="number" min="1" style={{ width: 80 }} value={filters.early_min_days || ''} placeholder="1" onChange={(e) => setFilter('early_min_days', e.target.value)} aria-label="Minimum days early" /><span className="muted">days early</span></label>
          )}
        </div>
      )}
      <DataTable columns={columns} paged={paged} rowKey={(r) => r.s_no} onRowClick={onRowClick} noun="placements" />
    </>
  );
}
